/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { IncorrectProblem } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/IncorrectProblem';
import { OverallIntentStats } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/OverallIntentStats';
import { TitledSection } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/TitledSection';
import { UnclearProblem } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/UnclearProblem';
import { FaultyIntent } from 'src/luis/projects/analytics/types/FaultyIntent';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

export type AccuracySectionProps = {
    /**
     * The select faulty intent.
     */
    selectedIntent: IntentMetadata;

    /**
     * Array of the intents that conflict with the current intent
     * where the conflict is misclassification. e.g the utterances
     * that belong to the current intent and predicted as one of
     * this array intents.
     */
    incorrectlyConflictedIntents: FaultyIntent[];

    /**
     * Array of the intents that conflict with the current intent
     * where the conflict is ambiguity. e.g the utterances that
     * belong to the current intent but they are also highly predicted
     * to another intents with small difference between the the
     * prediction of the ambiguous intent and the labeled intent.
     */
    unclearConflictedIntents: FaultyIntent[];

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick?: (intentId: LuisId, filters: PassedFilters) => void;
};

const messages = defineMessages({
    accuracySectionTitle: {
        id: 'analytics.faultyIntentDetails.accuracySectionTitle',
        defaultMessage: 'Accuracy'
    }
});

/**
 * The sections to visualize the details of the selected intent:
 * 1. Overall insights for the selected intent.
 * 2. Incorrect problem information if it exists in the selected intent.
 * 3. Unclear problem information if it exists in the selected intent.
 *
 * @param props The properties to visualize this section.
 */
export const AccuracySection = (props: AccuracySectionProps) => {
    const { selectedIntent, incorrectlyConflictedIntents, unclearConflictedIntents, onIntentClick } = props;
    const { localize } = useLocalization();

    return (
        <TitledSection title={localize(messages.accuracySectionTitle)}>
            <Stack tokens={{ childrenGap: 40 }}>
                <OverallIntentStats selectedIntent={selectedIntent} onIntentClick={onIntentClick} />

                {incorrectlyConflictedIntents.length && (
                    <IncorrectProblem
                        selectedIntent={selectedIntent}
                        incorrectIntents={incorrectlyConflictedIntents}
                        onIntentClick={onIntentClick}
                    />
                )}

                {unclearConflictedIntents.length && (
                    <UnclearProblem
                        selectedIntent={selectedIntent}
                        engagedIntents={unclearConflictedIntents}
                        onIntentClick={onIntentClick}
                    />
                )}
            </Stack>
        </TitledSection>
    );
};
